import { NotebookTextIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BackLink, DataTable, EmptyState, ErrorNotice, PageHeading, Panel, Pill, SearchInput, SkeletonTable, type DataTableSort } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatEventDate } from '@/lib/event-time';
import { noteCategoryLabel } from '@/lib/events';
import { filterRows, sortRows } from '@/lib/facet-table';
import { listTemplateUsage, templateStatusTone, type TemplateUsageNote } from '@/lib/templates';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

/** Sortable columns; keys are flattened row fields. */
const USAGE_SORT_FIELDS = {
  event: 'eventName',
  eventDate: 'eventDateMs',
  note: 'subject',
  updated: 'updatedMs',
  author: 'authorName',
} as const;
const USAGE_SORT_KEYS = Object.values(USAGE_SORT_FIELDS);
const DEFAULT_USAGE_SORT: DataTableSort = { key: USAGE_SORT_FIELDS.updated, direction: 'desc' };

/** A note plus the flattened fields the table sorts and searches on. */
interface UsageRow extends TemplateUsageNote {
  readonly eventName: string;
  readonly eventDateText: string;
  readonly eventDateMs: number | null;
  readonly contractNumber: string;
  readonly updatedMs: number | null;
  readonly updatedText: string;
  readonly authorName: string;
  readonly statusText: string;
  /** The event's Notes tab, where the note is. */
  readonly href: string;
}

function msOrNull(iso: string | undefined): number | null {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? null : ms;
}

function usageRow(note: TemplateUsageNote): UsageRow {
  const updatedMs = msOrNull(note.updatedAt);
  return {
    ...note,
    eventName: note.event.name,
    eventDateText: (note.event.eventDate ? formatEventDate({ eventDate: note.event.eventDate }, 'medium') : undefined) ?? 'No date',
    eventDateMs: msOrNull(note.event.eventDate),
    contractNumber: note.event.contractNumber ?? '',
    updatedMs,
    updatedText: updatedMs === null ? '—' : new Date(updatedMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    authorName: note.author?.name ?? 'Unknown',
    statusText: note.status ?? '',
    href: `/events/${note.event.id}?tab=notes`,
  };
}

/**
 * Where one note template is used: every live note that follows it, with
 * its event, title, last update and author, newest first. Admin-only like
 * the rest of template management; the API enforces it.
 *
 * @returns The usage page.
 */
export function TemplateUsagePage(): React.ReactElement {
  const { templateId = '' } = useParams<{ templateId: string }>();
  const { search, setSearch } = useUrlSearch();
  const { sort, onSort } = useTableSort(USAGE_SORT_KEYS, DEFAULT_USAGE_SORT);
  const usage = useQuery({
    queryKey: ['template-usage', templateId],
    queryFn: ({ signal }) => listTemplateUsage(templateId, signal),
    enabled: templateId !== '',
  });

  const rows = usage.data
    ? sortRows(filterRows(usage.data.notes.map(usageRow), search, ['eventName', 'contractNumber', 'subject', 'authorName', 'statusText']), sort)
    : [];
  const name = usage.data?.template.name ?? 'Template';
  const total = usage.data?.total ?? 0;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/templates">All note templates</BackLink>
      <div className="mt-4">
        <PageHeading
          eyebrow="NOTE TEMPLATE"
          title={`Where ${name} is used`}
          subtitle={
            usage.data
              ? `${total} note${total === 1 ? '' : 's'} follow this template. Archived notes are left out.`
              : 'The notes that follow this template, newest first.'
          }
        />
      </div>

      <div className="mt-6 space-y-4">
        {usage.isPending ? (
          <SkeletonTable label="Loading notes…" />
        ) : usage.isError ? (
          <ErrorNotice title="Usage unavailable" message={errorMessage(usage.error)} />
        ) : usage.data.notes.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<NotebookTextIcon aria-hidden className="size-6" />}
              title="No notes use this template yet"
              message="Notes written with this template on an event's Notes tab will be listed here, with the event and who wrote them."
            />
          </Panel>
        ) : (
          <>
            <SearchInput
              label="Search notes"
              placeholder="Event, note, status or author"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="max-w-sm"
            />
            {usage.data.total > usage.data.notes.length ? (
              <p className="text-portal-micro text-portal-muted">
                Showing the {usage.data.notes.length} most recently updated of {usage.data.total}.
              </p>
            ) : null}
            {rows.length === 0 ? (
              <Panel>
                <EmptyState title="No notes match" message="Try a different search." />
              </Panel>
            ) : (
              <DataTable
                headers={[
                  { label: 'Event', sortKey: USAGE_SORT_FIELDS.event },
                  { label: 'Event date', sortKey: USAGE_SORT_FIELDS.eventDate },
                  { label: 'Note', sortKey: USAGE_SORT_FIELDS.note },
                  { label: 'Last updated', sortKey: USAGE_SORT_FIELDS.updated },
                  { label: 'Author', sortKey: USAGE_SORT_FIELDS.author },
                ]}
                sort={sort}
                onSort={onSort}
                minWidth={860}
                cards={rows.map((row) => ({
                  key: row.noteId,
                  title: (
                    <Link to={row.href} className="font-bold hover:underline">
                      {row.subject || 'Untitled note'}
                    </Link>
                  ),
                  subtitle: row.eventName,
                  fields: [
                    { label: 'Event date', value: row.eventDateText },
                    { label: 'Status', value: row.statusText || '—' },
                    { label: 'Last updated', value: row.updatedText },
                    { label: 'Author', value: row.authorName },
                  ],
                }))}
              >
                {rows.map((row) => (
                  <tr key={row.noteId} className="border-t border-portal-line align-middle">
                    <td className="p-3 text-portal-small">
                      <Link to={`/events/${row.event.id}`} className="block font-bold underline-offset-2 hover:underline">
                        {row.eventName}
                      </Link>
                      {row.contractNumber ? <span className="block text-portal-micro text-portal-muted">{row.contractNumber}</span> : null}
                    </td>
                    <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.eventDateText}</td>
                    <td className="p-3 text-portal-small">
                      <Link to={row.href} className="block underline-offset-2 hover:underline">
                        {row.subject || 'Untitled note'}
                      </Link>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-portal-micro text-portal-muted">
                        {noteCategoryLabel(row.category)}
                        {row.status ? <Pill tone={templateStatusTone(row.status)}>{row.status}</Pill> : null}
                      </span>
                    </td>
                    <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.updatedText}</td>
                    <td className="p-3 text-portal-small text-portal-muted">{row.authorName}</td>
                  </tr>
                ))}
              </DataTable>
            )}
          </>
        )}
      </div>
    </section>
  );
}
